import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  PasswordStrengthMeter,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  useAuth,
  validationErrorClassName,
  type AuthFormError,
} from '@monecolier/ui';
import { resetPassword } from '@monecolier/api-client';

const REDIRECT_AFTER_RESET = '/login';

// Miroir de apps/api/src/lib/password.ts#MIN_PASSWORD_LENGTH — voir le commentaire
// équivalent dans Register.tsx.
const MIN_PASSWORD_LENGTH = 8;

export function ResetPassword() {
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<AuthFormError | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setValidationError(null);

    // Validation côté client AVANT tout appel réseau — miroir de la validation
    // backend, même pattern que Register.tsx.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(t('resetPassword.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }

    setSubmitting(true);
    try {
      // `token` non-null garanti ici : le bouton de soumission est désactivé quand
      // `token` est absent (voir plus bas), ce chemin n'est donc atteignable qu'avec
      // un token présent dans l'URL.
      await resetPassword(apiClient, { token: token!, password });
      // Pas de connexion automatique (contrairement à Register.tsx) — POST
      // /auth/reset-password ne renvoie aucun token, par construction (AUT-02) :
      // toutes les sessions actives de l'utilisateur viennent d'être révoquées, se
      // reconnecter explicitement avec le nouveau mot de passe est le comportement
      // attendu. `state` plutôt qu'un paramètre d'URL pour le message de
      // confirmation : évite qu'il survive à un rechargement de /login (comportement
      // ponctuel, pas une donnée d'URL partageable/marque-page-able).
      navigate(REDIRECT_AFTER_RESET, {
        state: { passwordResetConfirmation: t('resetPassword.confirmation') },
      });
    } catch (err) {
      // describeAuthFormError() couvre déjà les 400 (token invalide/expiré/déjà
      // utilisé, mot de passe trop court) : le message backend est repris tel quel,
      // clair et distinct pour chaque cas (voir apps/api/src/routes/auth.ts) — pas de
      // reformulation ici.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Lien sans token exploitable (paramètre absent, ou vidé manuellement dans la barre
  // d'adresse) : message clair et distinct du cas nominal, PAS un formulaire qui
  // échouerait silencieusement ou planterait sur `token!` ci-dessus (ticket AUT-02,
  // point 3 du volet frontend).
  if (!token) {
    return (
      <Card title={t('resetPassword.title')}>
        <p role="alert" data-testid="reset-password-missing-token" className={validationErrorClassName}>
          {t('resetPassword.missingToken')}
        </p>
        <p className={secondaryTextClassName}>
          <Link to="/forgot-password" className={linkClassName}>
            {t('resetPassword.requestNewLink')}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('resetPassword.title')}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          {t('resetPassword.newPassword')}
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
            className={formInputClassName}
          />
        </label>
        <PasswordStrengthMeter password={password} />
        {validationError ? (
          <p role="alert" data-testid="reset-password-validation-error" className={validationErrorClassName}>
            {validationError}
          </p>
        ) : null}
        {error ? (
          <p role="alert" data-testid="reset-password-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </Button>
      </form>
    </Card>
  );
}
